/**
 * Late fees — one folded line on Billing (owner, Nov 1 2026): what the daily job does, and "Run now".
 * The rules and the numbers are Settings → Billing; nothing here texts or emails a customer.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AlarmClock, ChevronDown } from 'lucide-react';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

export default function LateFeesCard() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  const run = useMutation(() => api.post('/invoices/late-fees/run').then(r => r.data), {
    onSuccess: (d: any) => { setLast(d.message); toast.success(d.message, { autoClose: 9000 }); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not run the late fees'); },
  });
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900"><AlarmClock className="h-4 w-4 text-gray-500" /> Late fees</span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 text-sm text-gray-700">
          <p>Every morning: a bill sent 10 or more days ago with money still owed gets a one-time late fee; 30 or more days, interest on what's owed on the work for each 30-day period. Never on paid, voided or $0 bills, a customer marked exempt, or bills made before the start date. Fees are their own lines on the bill; waive one from the bill with the Waive button. Nobody is texted.</p>
          <p className="text-xs text-gray-500">The amounts and the start date: <Link to="/settings#late-fees" className="underline">Settings → Billing → Late fees</Link>. Running it twice adds nothing twice.</p>
          <div className="flex items-center gap-3">
            <button type="button" disabled={readOnly || run.isLoading} onClick={() => run.mutate()} className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-60">{run.isLoading ? 'Running…' : 'Run late fees now'}</button>
            {last && <span className="text-xs text-gray-600">{last}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
